<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Colors Component
      p.text--secondary Color system for consistent theming and design across the application.

    // Color Palette
    .component-section.mb-8#color-palette
      h3.mb-4.cursor-pointer(@click="navigateToSection('color-palette')") Color Palette
      .variations-grid
        .variation-item.mb-6(v-for="colorGroup in colorGroups" :key="colorGroup.name" :id="colorGroup.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(colorGroup.id)") {{ colorGroup.name }}
            p.text--secondary.caption {{ colorGroup.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .d-flex.flex-wrap.gap-3
              .color-swatch(v-for="color in colorGroup.colors" :key="color.name")
                .color-circle(:style="{ backgroundColor: color.value }" :class="{ 'white-border': color.value === '#ffffff' }")
                .color-info.mt-2
                  .color-name.text-caption.font-weight-medium {{ color.name }}
                  .color-value.text-caption.text--secondary {{ color.value }}
                  .color-variable.text-caption.font-family-monospace {{ color.variable }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === colorGroup.cssCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === colorGroup.cssCode ? 'success' : ''" @click="copyCode(colorGroup.cssCode)")
              span.font-weight-medium.ml-2 CSS Variables

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ colorGroup.cssCode }}

    // Utility Classes
    .component-section.mb-8#utility-classes
      h3.mb-4.cursor-pointer(@click="navigateToSection('utility-classes')") Utility Classes
      .variations-grid
        .variation-item.mb-6(v-for="utilityType in utilityTypes" :key="utilityType.name" :id="utilityType.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(utilityType.id)") {{ utilityType.name }}
            p.text--secondary.caption {{ utilityType.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .d-flex.flex-wrap.gap-4
              .utility-example(v-for="example in utilityType.examples" :key="example.class")
                div(:class="example.class" style="padding: 12px 16px; border-radius: 4px; min-width: 120px; text-align: center;") {{ example.text }}
                .utility-class.text-caption.font-family-monospace.mt-1.text-center {{ example.class }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === utilityType.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === utilityType.code ? 'success' : ''" @click="copyCode(utilityType.code)")
              span.font-weight-medium.ml-2 Code Examples

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ utilityType.code }}

    // CSS Variables Documentation
    .component-section.mb-8#css-variables
      h3.mb-4.cursor-pointer(@click="navigateToSection('css-variables')") CSS Variables
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Variable
                th Value
                th Description
            tbody
              tr(v-for="variable in cssVariables" :key="variable.name")
                td.font-weight-medium.text-no-wrap.font-family-monospace {{ variable.name }}
                td.text--secondary.text-no-wrap
                  .color-circle.d-inline-block.mr-2(:style="{ backgroundColor: variable.value, width: '16px', height: '16px', verticalAlign: 'middle' }" :class="{ 'white-border': variable.value === '#ffffff' }")
                  span {{ variable.value }}
                td.single-line {{ variable.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      colorGroups: [
        {
          name: 'Base Colors',
          id: 'base-colors',
          description: 'Fundamental colors used throughout the system',
          colors: [
            {name: 'White', value: '#ffffff', variable: '--yz-white'},
            {name: 'Black', value: '#000000', variable: '--yz-black'},
            {name: 'Transparent', value: 'transparent', variable: '--yz-transparent'}
          ],
          cssCode: `/* Base colors */
--yz-white: #ffffff;
--yz-black: #000000;
--yz-transparent: transparent;`
        },
        {
          name: 'Neutral Colors',
          id: 'neutral-colors',
          description: 'Grayscale colors for UI elements and text',
          colors: [
            {name: 'Neutral', value: '#333333', variable: '--yz-neutral'},
            {name: 'Neutral 100', value: '#FFFFFF', variable: '--yz-neutral-100'},
            {name: 'Neutral 200', value: '#F9FAFB', variable: '--yz-neutral-200'},
            {name: 'Neutral 300', value: '#EEEEEE', variable: '--yz-neutral-300'},
            {name: 'Neutral 400', value: '#C1C1C1', variable: '--yz-neutral-400'},
            {name: 'Neutral 500', value: '#848484', variable: '--yz-neutral-500'},
            {name: 'Neutral 600', value: '#525252', variable: '--yz-neutral-600'},
            {name: 'Neutral 700', value: '#333333', variable: '--yz-neutral-700'},
            {name: 'Neutral 800', value: '#1F1F1F', variable: '--yz-neutral-800'},
            {name: 'Neutral 900', value: '#000000', variable: '--yz-neutral-900'}
          ],
          cssCode: `/* Neutral colors */
--yz-neutral: #333333;
--yz-neutral-100: #FFFFFF;
--yz-neutral-200: #F9FAFB;
--yz-neutral-300: #EEEEEE;
--yz-neutral-400: #C1C1C1;
--yz-neutral-500: #848484;
--yz-neutral-600: #525252;
--yz-neutral-700: #333333;
--yz-neutral-800: #1F1F1F;
--yz-neutral-900: #000000;`
        },
        {
          name: 'Success Colors',
          id: 'success-colors',
          description: 'Green colors for positive states and success messages',
          colors: [
            {name: 'Success', value: '#14AE5C', variable: '--yz-success'},
            {name: 'Success 500', value: '#14AE5C', variable: '--yz-success-500'},
            {name: 'Success 700', value: '#259143', variable: '--yz-success-700'},
            {name: 'Green', value: '#1A7935', variable: '--yz-green'},
            {name: 'Green 100', value: '#EEFCE9', variable: '--yz-green-100'},
            {name: 'Green 500', value: '#1A7935', variable: '--yz-green-500'}
          ],
          cssCode: `/* Success colors */
--yz-success: #14AE5C;
--yz-success-500: #14AE5C;
--yz-success-700: #259143;
--yz-green: #1A7935;
--yz-green-100: #EEFCE9;
--yz-green-500: #1A7935;`
        },
        {
          name: 'Warning Colors',
          id: 'warning-colors',
          description: 'Orange and yellow colors for warning states',
          colors: [
            {name: 'Warning', value: '#EB7100', variable: '--yz-warning'},
            {name: 'Warning 100', value: '#FFFAE6', variable: '--yz-warning-100'},
            {name: 'Warning 500', value: '#EB7100', variable: '--yz-warning-500'},
            {name: 'Yellow', value: '#CA6C16', variable: '--yz-yellow'},
            {name: 'Yellow 100', value: '#FFF7D9', variable: '--yz-yellow-100'},
            {name: 'Yellow 500', value: '#CA6C16', variable: '--yz-yellow-500'}
          ],
          cssCode: `/* Warning colors */
--yz-warning: #EB7100;
--yz-warning-100: #FFFAE6;
--yz-warning-500: #EB7100;
--yz-yellow: #CA6C16;
--yz-yellow-100: #FFF7D9;
--yz-yellow-500: #CA6C16;`
        },
        {
          name: 'Danger Colors',
          id: 'danger-colors',
          description: 'Red colors for error states and destructive actions',
          colors: [
            {name: 'Danger', value: '#C82F2F', variable: '--yz-danger'},
            {name: 'Danger 500', value: '#C82F2F', variable: '--yz-danger-500'},
            {name: 'Danger 700', value: '#9C2525', variable: '--yz-danger-700'},
            {name: 'Red', value: '#B52222', variable: '--yz-red'},
            {name: 'Red 100', value: '#FFEBEB', variable: '--yz-red-100'},
            {name: 'Red 500', value: '#B52222', variable: '--yz-red-500'}
          ],
          cssCode: `/* Danger colors */
--yz-danger: #C82F2F;
--yz-danger-500: #C82F2F;
--yz-danger-700: #9C2525;
--yz-red: #B52222;
--yz-red-100: #FFEBEB;
--yz-red-500: #B52222;`
        },
        {
          name: 'Blue Colors',
          id: 'blue-colors',
          description: 'Blue colors for informational states and links',
          colors: [
            {name: 'Blue', value: '#3570CA', variable: '--yz-blue'},
            {name: 'Blue 100', value: '#F0F8FF', variable: '--yz-blue-100'},
            {name: 'Blue 500', value: '#3570CA', variable: '--yz-blue-500'}
          ],
          cssCode: `/* Blue colors */
--yz-blue: #3570CA;
--yz-blue-100: #F0F8FF;
--yz-blue-500: #3570CA;`
        }
      ],
      utilityTypes: [
        {
          name: 'Text Colors',
          id: 'text-colors',
          description: 'Utility classes for text colors',
          examples: [
            {class: 'text-neutral-700', text: 'Neutral 700'},
            {class: 'text-success', text: 'Success'},
            {class: 'text-warning', text: 'Warning'},
            {class: 'text-danger', text: 'Danger'},
            {class: 'text-blue', text: 'Blue'}
          ],
          code: `// Text color utilities
.text-neutral-700 Neutral text
.text-success Success text
.text-warning Warning text
.text-danger Danger text
.text-blue Blue text`
        },
        {
          name: 'Background Colors',
          id: 'background-colors',
          description: 'Utility classes for background colors',
          examples: [
            {class: 'bg-neutral-200 text-neutral-700', text: 'Neutral 200'},
            {class: 'bg-success text-white', text: 'Success'},
            {class: 'bg-warning-100 text-warning', text: 'Warning 100'},
            {class: 'bg-danger text-white', text: 'Danger'},
            {class: 'bg-blue-100 text-blue', text: 'Blue 100'}
          ],
          code: `// Background color utilities
.bg-neutral-200 Neutral background
.bg-success Success background
.bg-warning-100 Warning background
.bg-danger Danger background
.bg-blue-100 Blue background`
        },
        {
          name: 'Border Colors',
          id: 'border-colors',
          description: 'Utility classes for border colors',
          examples: [
            {class: 'border-neutral-300 border-solid', text: 'Neutral 300'},
            {class: 'border-success border-solid', text: 'Success'},
            {class: 'border-warning border-solid', text: 'Warning'},
            {class: 'border-danger border-solid', text: 'Danger'},
            {class: 'border-blue border-solid', text: 'Blue'}
          ],
          code: `// Border color utilities
.border-solid.border-neutral-300 Neutral border
.border-solid.border-success Success border
.border-solid.border-warning Warning border
.border-solid.border-danger Danger border
.border-solid.border-blue Blue border`
        }
      ],
      cssVariables: [
        {
          name: '--yz-white',
          value: '#ffffff',
          description: 'Pure white color'
        },
        {
          name: '--yz-black',
          value: '#000000',
          description: 'Pure black color'
        },
        {
          name: '--yz-neutral',
          value: '#333333',
          description: 'Primary neutral color'
        },
        {
          name: '--yz-neutral-300',
          value: '#EEEEEE',
          description: 'Light neutral for borders and dividers'
        },
        {
          name: '--yz-neutral-500',
          value: '#848484',
          description: 'Medium neutral for secondary text'
        },
        {
          name: '--yz-neutral-700',
          value: '#333333',
          description: 'Dark neutral for primary text'
        },
        {
          name: '--yz-success',
          value: '#14AE5C',
          description: 'Primary success color'
        },
        {
          name: '--yz-warning',
          value: '#EB7100',
          description: 'Primary warning color'
        },
        {
          name: '--yz-danger',
          value: '#C82F2F',
          description: 'Primary danger color'
        },
        {
          name: '--yz-blue',
          value: '#3570CA',
          description: 'Primary blue color for links and info'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.color-swatch {
  text-align: center;
  min-width: 100px;
}

.color-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 0 auto;
  display: block;
}

.white-border {
  border: 1px solid #e0e0e0;
}

.color-info {
  text-align: center;
}

.color-name {
  font-size: 12px;
  font-weight: 600;
}

.color-value {
  font-size: 11px;
}

.color-variable {
  font-size: 10px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.utility-example {
  text-align: center;
}

.utility-class {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 11px;
}

.font-family-monospace {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}
</style>